import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import "../../i18n";
import { serverText } from "../../i18n/localized";
import { Notice, PILL_MAP, StatusPill, type PillTone } from "../../design/components";
import {
  useEntityUsers,
  usePatchMembership,
  useRevokeInvitation,
  useRoles,
  type RoleInfo,
  type UserRow,
  type UserState,
  type UsersSource,
} from "../../api/users";
import type { Role } from "../../api/types";
import { dateTime, relative } from "../../lib/format";
import { InviteDialog, userFailureMessage } from "./InviteDialog";
import { RoleSelect } from "./RoleSelect";
import styles from "./users.module.css";

/** m6-rulings.md #130: `PILL_MAP.active` reads "In progress" for entities, so users map locally.
    Only "active"'s label is this file's own literal text; `invited`/`suspended` reuse the shared
    PILL_MAP labels from design/components, which stay untranslated here (out of this task's scope). */
export const USER_PILL: Record<UserState, [PillTone, string]> = {
  active: ["ok", "Active"],
  invited: PILL_MAP.invited!,
  suspended: PILL_MAP.suspended!,
};

export const LAST_ADMIN_HINT = "This is the last administrator for this company";

/** Viewport room below the trigger before the menu opens upward instead. */
const MENU_ROOM = 220;

function rowLabel(row: UserRow): string {
  return row.name ?? row.email;
}

/** A plain function (not a hook), so it takes `t` from its caller rather than calling useTranslation itself. */
function lastSeen(row: UserRow, t: TFunction): string {
  if (row.kind === "invitation") {
    const by = row.invitedBy
      ? t("users.table.invitedBy", "Invited by {{name}}", { name: row.invitedBy })
      : t("users.table.invited", "Invited");
    return row.expiresAt
      ? t("users.table.invitedExpires", "{{by}} · expires {{when}}", { by, when: dateTime(row.expiresAt) })
      : by;
  }
  return row.lastSeenAt ? relative(row.lastSeenAt) : t("users.table.never", "Never");
}

type MenuAction = { label: string; onPick: () => void; disabled?: boolean; title?: string };

type MenuPlace = { left: number; top: number; up: boolean };

/* Rendered through a portal: `.table-wrap` scrolls, so an absolute menu below the last row is clipped. */
function RowMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const { t } = useTranslation();
  const [place, setPlace] = useState<MenuPlace | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const open = place !== null;

  function openMenu() {
    const rect = trigger.current!.getBoundingClientRect();
    const up = window.innerHeight - rect.bottom < MENU_ROOM;
    setPlace({ left: rect.right, top: up ? rect.top - 4 : rect.bottom + 4, up });
  }

  function close(returnFocus: boolean) {
    setPlace(null);
    if (returnFocus) trigger.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]:not(:disabled)")?.focus();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) setPlace(null);
    };
    const onScroll = () => setPlace(null);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    items[(at + step + items.length) % items.length]?.focus();
  }

  const style = place
    ? place.up
      ? { left: place.left, bottom: window.innerHeight - place.top, transform: "translateX(-100%)" }
      : { left: place.left, top: place.top, transform: "translateX(-100%)" }
    : undefined;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="btn btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("users.table.actionsFor", "Actions for {{label}}", { label })}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            openMenu();
          }
        }}
      >
        {t("users.table.actions", "Actions")}
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className={styles.menu}
              style={style}
              role="menu"
              aria-label={t("users.table.actionsFor", "Actions for {{label}}", { label })}
              onKeyDown={onMenuKey}
            >
              {actions.map((action, index) => (
                <Fragment key={action.label}>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    disabled={action.disabled}
                    title={action.title}
                    aria-describedby={action.title ? `${hintId}-${index}` : undefined}
                    onClick={() => {
                      close(true);
                      action.onPick();
                    }}
                  >
                    {action.label}
                  </button>
                  {action.title ? (
                    <span id={`${hintId}-${index}`} className={styles.srOnly}>
                      {action.title}
                    </span>
                  ) : null}
                </Fragment>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export function UsersTable({
  entityId,
  source = "console",
  variant = "page",
}: {
  /** Required on the console; the portal's pinned company needs none (m6-rulings.md #132). */
  entityId?: string | undefined;
  source?: UsersSource | undefined;
  /** `tab` carries its own heading; the page supplies the h1. */
  variant?: "page" | "tab";
}) {
  const { t } = useTranslation();
  const scope = { source, entityId };
  const roles = useRoles();
  const users = useEntityUsers(scope);
  const patch = usePatchMembership(scope);
  const revoke = useRevokeInvitation(scope);
  const [dialog, setDialog] = useState<{ resend?: UserRow } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const catalogue: RoleInfo[] = roles.data ?? [];
  const roleName = (key: Role) => {
    const found = catalogue.find((role) => role.key === key);
    return found ? serverText(`domain.role.${key}.name`, found.name) : key;
  };
  const rows = users.data ?? [];
  const activeAdmins = rows.filter((row) => row.kind === "membership" && row.role === "entity_admin" && row.state === "active");

  const fail = (fallback: string) => (error: unknown) => setFailure(userFailureMessage(error, fallback));

  function setState(row: UserRow, state: "active" | "suspended") {
    setFailure(null);
    patch.mutate(
      { membershipId: row.membershipId!, state },
      { onError: fail(t("users.table.saveError", "The change could not be saved.")) },
    );
  }

  function changeRole(row: UserRow, role: Role) {
    setEditing(null);
    if (role === row.role) return;
    setFailure(null);
    patch.mutate(
      { membershipId: row.membershipId!, role },
      { onError: fail(t("users.table.roleChangeError", "The role could not be changed.")) },
    );
  }

  function actionsFor(row: UserRow): MenuAction[] {
    if (row.kind === "invitation") {
      return [
        { label: t("users.table.resendInvitation", "Resend invitation"), onPick: () => setDialog({ resend: row }) },
        {
          label: t("users.table.revokeInvitation", "Revoke invitation"),
          onPick: () => {
            setFailure(null);
            revoke.mutate(row.invitationId!, {
              onError: fail(t("users.table.revokeError", "The invitation could not be revoked.")),
            });
          },
        },
      ];
    }
    const lastAdmin = activeAdmins.length === 1 && activeAdmins[0]!.membershipId === row.membershipId;
    const actions: MenuAction[] = [
      { label: t("users.table.changeRole", "Change role"), onPick: () => setEditing(row.membershipId) },
    ];
    if (row.state === "suspended")
      actions.push({ label: t("users.table.restore", "Restore"), onPick: () => setState(row, "active") });
    else {
      actions.push({
        label: t("users.table.suspend", "Suspend"),
        onPick: () => setState(row, "suspended"),
        ...(lastAdmin ? { disabled: true, title: t("users.table.lastAdminHint", LAST_ADMIN_HINT) } : {}),
      });
    }
    return actions;
  }

  return (
    <section className={styles.section} aria-busy={users.isLoading}>
      <div className={styles.toolbar}>
        {variant === "tab" ? (
          <h2 className={styles.tabTitle}>{t("users.table.peopleWithAccess", "People with access")}</h2>
        ) : (
          <div className="grow" />
        )}
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setDialog({})}>
          {t("users.table.invitePerson", "Invite a person")}
        </button>
      </div>
      {failure ? (
        <div className={styles.notice}>
          <Notice kind="fail">{failure}</Notice>
        </div>
      ) : null}
      {users.isError ? (
        <Notice kind="fail">{t("users.table.loadError", "The people with access could not be loaded.")}</Notice>
      ) : !rows.length && !users.isLoading ? (
        <Notice kind="info">
          {t("users.table.emptyState", "Nobody has access to this company yet. Invite its first administrator.")}
        </Notice>
      ) : (
        <div className="table-wrap">
          <table className="tbl" aria-label={t("users.table.tableLabel", "Users")}>
            <thead>
              <tr>
                <th>{t("users.table.colName", "Name")}</th>
                <th>{t("users.table.colEmail", "Email")}</th>
                <th>{t("users.table.colRole", "Role")}</th>
                <th>{t("users.table.colStatus", "Status")}</th>
                <th>{t("users.table.colLastSeen", "Last seen")}</th>
                <th aria-label={t("users.table.actions", "Actions")} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const [tone, fallbackLabel] = USER_PILL[row.state];
                const label = row.state === "active" ? t("users.table.stateActive", "Active") : fallbackLabel;
                const key = row.membershipId ?? row.invitationId ?? row.email;
                return (
                  <tr key={key}>
                    <td>
                      {row.name ?? <span className={styles.subtle}>{t("users.table.notYetAccepted", "Not yet accepted")}</span>}
                      {row.isSelf ? <span className={`chip ${styles.selfChip}`}>{t("users.table.you", "You")}</span> : null}
                    </td>
                    <td className="mono">{row.email}</td>
                    <td>
                      {editing === row.membershipId && row.membershipId ? (
                        <RoleSelect
                          id={`role-${row.membershipId}`}
                          value={row.role}
                          roles={catalogue.filter((role) => role.invitable)}
                          ariaLabel={t("users.table.roleFor", "Role for {{name}}", { name: rowLabel(row) })}
                          autoFocus
                          onChange={(role) => changeRole(row, role)}
                          onCancel={() => setEditing(null)}
                        />
                      ) : (
                        roleName(row.role)
                      )}
                    </td>
                    <td>
                      <StatusPill status={row.state} tone={tone} label={label} />
                    </td>
                    <td className={styles.subtle}>{lastSeen(row, t)}</td>
                    <td className="r nowrap">
                      {row.isSelf ? null : <RowMenu label={rowLabel(row)} actions={actionsFor(row)} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dialog ? (
        <InviteDialog
          entityId={entityId}
          source={source}
          roles={catalogue}
          resend={dialog.resend}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}
